/** Learners: target, placement levels, access, EO credits, account-sharing alerts, exam readiness. */

import { type LearnerAdmin, useAcademyLearnersList } from "@agencyos/api-client";
import { Badge, GlassCard, Skeleton } from "@agencyos/ui-web";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { dateText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { ieltsTarget } from "./ieltsData";
import { targetOf } from "./ProgressTab";
import { isObject, Pager, str } from "./shared";

const SKILLS = ["co", "ce", "ee", "eo"] as const;

/** `placement.results[skill].cefr` written by the placement test. */
export function placementLevels(placement: unknown): { skill: string; level: string }[] {
  const results = isObject(placement) && isObject(placement.results) ? placement.results : {};
  return SKILLS.flatMap((skill) => {
    const row = results[skill];
    const level = isObject(row) ? str(row.cefr) : "";
    return level ? [{ skill, level }] : [];
  });
}

function LearnerRow({ learner }: { learner: LearnerAdmin }) {
  const { t, i18n } = useTranslation();
  // D-123: the goal, the date and the access of the exam shown (the IELTS, TEF and German lists
  // showed the TCF's « NCLC 7 » and its access)
  const { exam } = useAdminExam();
  const tcf = exam === "tcf_canada";
  const examDate = tcf ? learner.exam_date : ieltsTarget(learner.exam_profiles, exam).date || null;
  const access = tcf ? learner.has_access : learner.access[exam] === true;
  const levels = placementLevels(tcf ? learner.placement : learner.exam_profiles.find((p) => p.exam === exam)?.placement);
  return (
    <tr>
      <td>
        <strong>{learner.full_name || learner.email}</strong>
        <div className="muted">{learner.email}</div>
        {learner.beginner ? <Badge tone="offer">{t("academyAdmin.learners.beginner")}</Badge> : null}
      </td>
      <td className="nowrap">
        {targetOf(learner, exam, i18n.language, t).target}
        {examDate ? <div className="muted">{t("academyAdmin.learners.examOn", { date: dateText(examDate, i18n.language) })}</div> : null}
      </td>
      <td>
        {levels.length ? (
          <div className="row">
            {levels.map((l) => (
              <span key={l.skill} className="code-chip">
                {l.skill.toUpperCase()} {l.level}
              </span>
            ))}
          </div>
        ) : (
          <span className="muted">{t("academyAdmin.learners.noPlacement")}</span>
        )}
      </td>
      <td>
        {access ? (
          <Badge tone="success">
            {tcf && learner.active_until ? t("academyAdmin.learners.activeUntil", { date: dateText(learner.active_until, i18n.language) }) : t("academyAdmin.learners.active")}
          </Badge>
        ) : (
          <Badge>{t("academyAdmin.learners.noAccess")}</Badge>
        )}
      </td>
      <td>{learner.eo_credits}</td>
      <td>
        {learner.device_alerts > 0 ? (
          <Badge tone="danger">
            <ShieldAlert size={14} aria-hidden="true" /> {t("academyAdmin.learners.sharing", { count: learner.device_alerts })}
          </Badge>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>{learner.ready_by_exam?.[exam] === true ? <Badge tone="success">{t("academyAdmin.learners.ready")}</Badge> : <Badge>{t("academyAdmin.learners.notReady")}</Badge>}</td>
    </tr>
  );
}

export function LearnersTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { params } = useAdminExam();
  const list = useAcademyLearnersList({ page, ...params });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyAdmin.learners.title")}</h2>
          <p className="muted">{t("academyAdmin.learners.intro")}</p>
        </div>
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.learners.learner")}</th>
                  <th>{t("academyAdmin.learners.target")}</th>
                  <th>{t("academyAdmin.learners.placement")}</th>
                  <th>{t("academyAdmin.learners.access")}</th>
                  <th>{t("academyAdmin.learners.credits")}</th>
                  <th>{t("academyAdmin.learners.devices")}</th>
                  <th>{t("academyAdmin.learners.examReady")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((learner) => (
                  <LearnerRow key={learner.id} learner={learner} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          <p className="muted">{t("academyAdmin.learners.readyHint")}</p>
        </>
      ) : (
        <p className="muted">{t("academyAdmin.learners.empty")}</p>
      )}
    </GlassCard>
  );
}
